import { motion } from "framer-motion";
import { useEffect, useState, type RefObject } from "react";

import { ChatInput, type ChatInputRef } from "./ChatInput";
import { EMPTY_STATE_PRESETS } from "./constants/emptyStatePresets";
import {
  CHAT_EMPTY_STATE_DECORATION_DELAY_MS,
  CHAT_EMPTY_STATE_DECORATION_TRANSITION,
  CHAT_EMPTY_STATE_HEADING,
  CHAT_INPUT_LAYOUT_ID,
  CHAT_INPUT_LAYOUT_TRANSITION,
  CHAT_INPUT_PLACEHOLDER,
  CHAT_INPUT_SHADOW_CLASS,
} from "./constants";

import { Button } from "@/components/ui/button";

interface VibeChatEmptyStateProps {
  chatInputRef: RefObject<ChatInputRef>;
  inputValue: string;
  setInputValue: (value: string) => void;
  onSubmit: (message: string) => void;
  handleStop: () => void;
  isLoading: boolean;
}

const VibeChatEmptyState = ({
  chatInputRef,
  inputValue,
  setInputValue,
  onSubmit,
  handleStop,
  isLoading,
}: VibeChatEmptyStateProps) => {
  const [shouldShowDecorations, setShouldShowDecorations] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setShouldShowDecorations(true),
      CHAT_EMPTY_STATE_DECORATION_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, []);

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-10">
      <div className="flex w-full max-w-2xl flex-col items-center px-1">
        {shouldShowDecorations && (
          <motion.h3
            className="mb-5 text-center text-xl font-semibold text-gray-900"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={CHAT_EMPTY_STATE_DECORATION_TRANSITION}
          >
            {CHAT_EMPTY_STATE_HEADING}
          </motion.h3>
        )}
        {!shouldShowDecorations && <div className="mb-5 h-7" aria-hidden />}
        <motion.div
          layoutId={CHAT_INPUT_LAYOUT_ID}
          layout="position"
          transition={CHAT_INPUT_LAYOUT_TRANSITION}
          className="w-full"
        >
          <ChatInput
            ref={chatInputRef}
            value={inputValue}
            onChange={setInputValue}
            onSubmit={onSubmit}
            onStop={handleStop}
            isLoading={isLoading}
            placeholder={CHAT_INPUT_PLACEHOLDER}
            className={CHAT_INPUT_SHADOW_CLASS}
          />
        </motion.div>
        {shouldShowDecorations && (
          <motion.div
            className="mt-3 flex w-full flex-wrap justify-center gap-1.5"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={CHAT_EMPTY_STATE_DECORATION_TRANSITION}
          >
            {EMPTY_STATE_PRESETS.map(preset => {
              const PresetIcon = preset.icon;
              return (
                <Button
                  key={preset.label}
                  type="button"
                  variant="pill"
                  size="pill"
                  onClick={() => {
                    setInputValue(preset.prompt);
                    chatInputRef.current?.setValue(preset.prompt);
                  }}
                >
                  <PresetIcon className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
                  {preset.label}
                </Button>
              );
            })}
          </motion.div>
        )}
        {!shouldShowDecorations && <div className="mt-3 h-7" aria-hidden />}
      </div>
    </div>
  );
};

export default VibeChatEmptyState;
